import type { QuizDefinition, QuizQuestion } from "@quiz/quiz-core";
import { getQuestionTotal, getQuizPresentation, isCountedQuestion } from "@quiz/quiz-definitions";
import { ArrowRight, createElement } from "lucide";
import { button, el } from "../utils";

export interface EmbeddedQuestionOptions {
  quiz: QuizDefinition;
  question: QuizQuestion;
  selectedAnswerId?: string;
  compact?: boolean;
  visual?: boolean;
  progress?: number;
  onAnswer: (answerId: string, button: HTMLButtonElement) => void;
}

export function renderEmbeddedQuestion(options: EmbeddedQuestionOptions): HTMLElement {
  const card = el("div", `embedded-question${options.compact ? " embedded-question--compact" : ""}`);
  const portraits = getQuizPresentation(options.quiz).firstQuestionLayout === "portrait-pair";
  if (portraits) card.classList.add("embedded-question--portraits");
  const showCounter = isCountedQuestion(options.quiz, options.question.id);
  if (options.progress !== undefined && showCounter) {
    const progress = el("div", "embedded-question__progress");
    const bar = el("div", "embedded-question__progress-bar");
    bar.style.width = `${options.progress}%`;
    progress.append(bar);
    card.append(progress);
  }

  const prompt = el("div", "embedded-question__prompt");
  const total = getQuestionTotal(options.quiz);
  if (total && showCounter) prompt.append(el("span", "question-count", `1 / ${total}`));
  prompt.append(el("p", "embedded-question__kicker", options.quiz.metadata.name));
  prompt.append(el("h2", "embedded-question__title", options.question.title));
  if (options.question.subtitle) prompt.append(el("p", "embedded-question__subtitle", options.question.subtitle));
  card.append(prompt);

  const list = el("div", `embedded-answer-list${options.visual ? " embedded-answer-list--visual" : ""}`);
  list.append(
    ...options.question.answers.map((answer) => {
      const item = button("embedded-answer", "", () => options.onAnswer(answer.id, item));
      item.dataset.answerId = answer.id;
      if (answer.media) {
        const image = el("img", "embedded-answer__image") as HTMLImageElement;
        image.src = answer.media.src;
        image.alt = "";
        image.loading = "eager";
        const crop = getQuizPresentation(options.quiz).portraitCrops?.[answer.id];
        if (portraits && crop) {
          const media = el("span", "embedded-answer__media");
          if (crop.scale !== undefined) image.style.setProperty("--portrait-scale", String(crop.scale));
          if (crop.objectPosition) image.style.objectPosition = crop.objectPosition;
          if (crop.offsetY) image.style.setProperty("--portrait-offset-y", crop.offsetY);
          if (crop.transformOrigin) image.style.setProperty("--portrait-origin", crop.transformOrigin);
          if (crop.mobileTransformOrigin) image.style.setProperty("--portrait-mobile-origin", crop.mobileTransformOrigin);
          if (crop.background) media.style.background = crop.background;
          media.append(image);
          item.append(media);
        } else item.append(image);
      }
      const text = el("span", "embedded-answer__text");
      item.setAttribute("aria-label", answer.label);
      if (answer.emoji) {
        const emoji = el("span", "embedded-answer__emoji", answer.emoji);
        emoji.setAttribute("aria-hidden", "true");
        item.append(emoji);
      }
      text.append(el("strong", "", answer.label));
      if (answer.description) text.append(el("small", "", answer.description));
      const mark = el("span", "embedded-answer__mark");
      mark.setAttribute("aria-hidden", "true");
      if (portraits) mark.append(createElement(ArrowRight, { width: 20, height: 20, "stroke-width": 2 }));
      item.append(text, mark);
      if (answer.id === options.selectedAnswerId) item.classList.add("is-selected");
      return item;
    })
  );
  card.append(list);
  return card;
}

export const embeddedQuestionShadowStyles = `
  .embedded-question{font-family:var(--font-family, Manrope, system-ui, sans-serif);background:#fff;color:#102033;border-radius:28px;padding:26px;box-shadow:0 24px 70px rgba(13,28,45,.18)}
  .embedded-question--compact{padding:20px;border-radius:22px;box-shadow:0 16px 40px rgba(13,28,45,.14)}
  .embedded-question__progress{height:7px;background:#e7eef4;border-radius:999px;overflow:hidden;margin-bottom:20px}
  .embedded-question__progress-bar{height:100%;background:#0ba95b;border-radius:inherit;transition:width .22s ease}
  .embedded-question__kicker{margin:0 0 8px;color:#0b8e4e;font-weight:700;font-size:12px;text-transform:uppercase;letter-spacing:0}
  .embedded-question__title{margin:0;font-size:30px;line-height:1.16;font-weight:750}
  .embedded-question__subtitle{margin:10px 0 0;color:#607083;line-height:1.45}
  .embedded-answer-list{display:grid;gap:12px;margin-top:22px}
  .embedded-answer-list--visual{grid-template-columns:repeat(3,minmax(0,1fr))}
  .embedded-answer{display:grid;grid-template-columns:1fr auto;align-items:center;gap:14px;min-height:70px;width:100%;border:1px solid #dbe6ef;border-radius:18px;background:#fff;color:#102033;padding:16px 18px;text-align:left;font:inherit;cursor:pointer;transition:background .18s ease,border-color .18s ease,transform .18s ease}
  .embedded-answer:hover{transform:translateY(-1px);background:#f8fbfd}
  .embedded-answer.is-selected{border-color:#0ba95b;background:#eafff2}
  .embedded-answer__image{width:100%;aspect-ratio:4/3;object-fit:cover;border-radius:14px;margin-bottom:10px;grid-column:1/-1}
  .embedded-answer__text{display:grid;gap:5px;font-size:16px;font-weight:600}
  .embedded-answer__text small{color:#68798a;font-weight:500;line-height:1.35}
  .embedded-answer__mark{display:grid;place-items:center;width:28px;height:28px;border:1px solid #cad8e4;border-radius:999px;color:#0ba95b;font-weight:800}
  .embedded-answer.is-selected .embedded-answer__mark{background:#0b8e4e;border-color:#0b8e4e}
  .embedded-answer.is-selected .embedded-answer__mark::after{content:"";width:9px;height:5px;border:solid white;border-width:0 0 2px 2px;transform:rotate(-45deg)}
  .embedded-answer:focus-visible{outline:3px solid #6267b2;outline-offset:3px}
  @media(max-width:760px){.embedded-answer-list--visual{grid-template-columns:1fr}.embedded-question{padding:20px;border-radius:22px}.embedded-answer{min-height:62px;padding:14px 15px}}
  @media(prefers-reduced-motion:reduce){.embedded-answer,.embedded-question__progress-bar{transition:none}}
`;
